import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';

export interface LinkedHabit {
  id: string;
  title: string;
  streak: number;
  completedDates: string[];
  goalId: string;
  actionId: string;
}

export interface ActionLinkedHabits {
  [actionId: string]: LinkedHabit[];
}

export interface ActionProgress {
  milestoneIndex: number;
  actionIndex: number;
  actionId: string | null;
  isManuallyCompleted: boolean;
  hasLinkedHabits: boolean;
  habitProgress: number; // 0-100
  linkedHabitsCount: number;
  completionsLast30Days: number;
}

export interface ProgressStats {
  completed: number;
  total: number;
  percentage: number;
  habitCompletionsThisWeek: number;
  actionsWithHabitProgress: number;
}

// Helper to get dates for last N days
const getDateRange = (days: number): string[] => {
  const dates: string[] = [];
  const today = new Date();
  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(format(date, 'yyyy-MM-dd'));
  }
  return dates;
};

export const useActionLinkedHabits = (planId: string | null) => {
  const { user } = useAuth();
  const [linkedHabits, setLinkedHabits] = useState<ActionLinkedHabits>({});
  const [milestoneActionsMap, setMilestoneActionsMap] = useState<Array<{
    id: string;
    milestone_index: number;
    action_index: number;
    completed: boolean;
  }>>([]);
  const [loading, setLoading] = useState(false);

  const fetchLinkedHabits = useCallback(async () => {
    if (!user || !planId) return;
    setLoading(true);

    try {
      // First get all milestone_actions for this plan
      const { data: actionsData, error: actionsError } = await supabase
        .from('milestone_actions')
        .select('id, milestone_index, action_index, completed')
        .eq('plan_id', planId);

      if (actionsError) throw actionsError;
      
      setMilestoneActionsMap(actionsData || []);
      
      if (!actionsData || actionsData.length === 0) {
        setLinkedHabits({});
        setLoading(false);
        return;
      }

      const actionIds = actionsData.map(a => a.id);

      // Get all habits linked to these actions
      const { data: habitsData, error: habitsError } = await supabase
        .from('habits')
        .select('id, title, streak, completed_dates, goal_id, linked_action_id')
        .in('linked_action_id', actionIds);

      if (habitsError) throw habitsError;

      // Group habits by action ID
      const grouped: ActionLinkedHabits = {};
      habitsData?.forEach(habit => {
        if (habit.linked_action_id) {
          if (!grouped[habit.linked_action_id]) {
            grouped[habit.linked_action_id] = [];
          }
          grouped[habit.linked_action_id].push({
            id: habit.id,
            title: habit.title,
            streak: habit.streak,
            completedDates: habit.completed_dates || [],
            goalId: habit.goal_id,
            actionId: habit.linked_action_id,
          });
        }
      });

      setLinkedHabits(grouped);
    } catch (error) {
      console.error('Error fetching linked habits:', error);
    } finally {
      setLoading(false);
    }
  }, [user, planId]);

  useEffect(() => {
    fetchLinkedHabits();
  }, [fetchLinkedHabits]);

  // Get habits linked to a specific action by milestone/action index
  const getHabitsForAction = useCallback(
    (milestoneIndex: number, actionIndex: number, milestoneActions: Array<{ id: string; milestone_index: number; action_index: number }>) => {
      const action = milestoneActions.find(
        a => a.milestone_index === milestoneIndex && a.action_index === actionIndex
      );
      if (!action) return [];
      return linkedHabits[action.id] || [];
    },
    [linkedHabits]
  );

  // Check if any habits for this action were completed today
  const getCompletedTodayCount = useCallback(
    (habits: LinkedHabit[]) => {
      const today = format(new Date(), 'yyyy-MM-dd');
      return habits.filter(h => h.completedDates.includes(today)).length;
    },
    []
  );

  // Calculate habit-based progress for a single action (last 30 days)
  const calculateActionHabitProgress = useCallback(
    (habits: LinkedHabit[]): { progress: number; completionsLast30Days: number } => {
      if (habits.length === 0) return { progress: 0, completionsLast30Days: 0 };

      const last30Days = getDateRange(30);
      let totalCompletions = 0;

      habits.forEach(habit => {
        const completionsInRange = habit.completedDates.filter(date => 
          last30Days.includes(date)
        ).length;
        totalCompletions += completionsInRange;
      });

      // Max possible = 30 days × number of habits
      const maxPossible = 30 * habits.length;
      const progress = Math.min(100, Math.round((totalCompletions / maxPossible) * 100));

      return { progress, completionsLast30Days: totalCompletions };
    },
    []
  );

  // Get habit completions this week
  const habitCompletionsThisWeek = useMemo(() => {
    const last7Days = getDateRange(7);
    let totalCompletions = 0;

    Object.values(linkedHabits).forEach(habits => {
      habits.forEach(habit => {
        const completionsInRange = habit.completedDates.filter(date => 
          last7Days.includes(date)
        ).length;
        totalCompletions += completionsInRange;
      });
    });

    return totalCompletions;
  }, [linkedHabits]);

  // Calculate enhanced progress for all actions
  const calculateEnhancedProgress = useCallback(
    (milestones: { actions: string[] }[], completedActions: Array<{ milestone_index: number; action_index: number; completed: boolean }>): ProgressStats => {
      let totalProgress = 0;
      let totalActions = 0;
      let manuallyCompleted = 0;
      let actionsWithHabitProgress = 0;

      milestones.forEach((milestone, mIndex) => {
        milestone.actions.forEach((_, aIndex) => {
          totalActions++;

          // Check if manually completed
          const actionRecord = completedActions.find(
            a => a.milestone_index === mIndex && a.action_index === aIndex && a.completed
          );

          if (actionRecord?.completed) {
            // Manually completed = 100%
            totalProgress += 100;
            manuallyCompleted++;
          } else {
            // Check for linked habits
            const actionId = milestoneActionsMap.find(
              a => a.milestone_index === mIndex && a.action_index === aIndex
            )?.id;

            if (actionId && linkedHabits[actionId]?.length > 0) {
              const { progress } = calculateActionHabitProgress(linkedHabits[actionId]);
              totalProgress += progress;
              if (progress > 0) actionsWithHabitProgress++;
            }
            // No linked habits and not completed = 0%
          }
        });
      });

      const percentage = totalActions > 0 ? Math.round(totalProgress / totalActions) : 0;

      return {
        completed: manuallyCompleted,
        total: totalActions,
        percentage,
        habitCompletionsThisWeek,
        actionsWithHabitProgress,
      };
    },
    [linkedHabits, milestoneActionsMap, calculateActionHabitProgress, habitCompletionsThisWeek]
  );

  // Get progress for a specific action
  const getActionProgress = useCallback(
    (milestoneIndex: number, actionIndex: number, completedActions: Array<{ milestone_index: number; action_index: number; completed: boolean }>): ActionProgress => {
      const actionRecord = completedActions.find(
        a => a.milestone_index === milestoneIndex && a.action_index === actionIndex
      );

      const actionFromMap = milestoneActionsMap.find(
        a => a.milestone_index === milestoneIndex && a.action_index === actionIndex
      );

      const actionId = actionFromMap?.id || null;
      const habits = actionId ? linkedHabits[actionId] || [] : [];
      const { progress, completionsLast30Days } = calculateActionHabitProgress(habits);

      return {
        milestoneIndex,
        actionIndex,
        actionId,
        isManuallyCompleted: actionRecord?.completed || false,
        hasLinkedHabits: habits.length > 0,
        habitProgress: progress,
        linkedHabitsCount: habits.length,
        completionsLast30Days,
      };
    },
    [linkedHabits, milestoneActionsMap, calculateActionHabitProgress]
  );

  return {
    linkedHabits,
    milestoneActionsMap,
    loading,
    getHabitsForAction,
    getCompletedTodayCount,
    calculateActionHabitProgress,
    calculateEnhancedProgress,
    getActionProgress,
    habitCompletionsThisWeek,
    refetch: fetchLinkedHabits,
  };
};
